import { useCallback } from 'react'
import tw, { styled } from 'twin.macro'
import range from 'lodash/range'

import PrevButton from './buttons/PrevButton'
import NumberButton from './buttons/NumberButton'
import NextButton from './buttons/NextButton'

const Container = styled.div`
  ${tw`flex justify-center text-gray-400`};

  * + * {
    margin-left: 4px;
  }
`

const MobileContainer = styled(Container)`
  ${tw`md:hidden`}
`

const DesktopContainer = styled(Container)`
  ${tw`hidden md:flex`}
`

type Props = {
  currentPage: number
  totalPage: number
  pageUrl?: (page: number) => string
  shouldNoFollow?: (page: number) => boolean
}

function Pagination(props: Props) {
  const { currentPage, totalPage, pageUrl, shouldNoFollow } = props

  const renderButtons = useCallback(
    (isMobile) => {
      const createNumberButtons: () => ('...' | number)[] = () => {
        const totalLength = isMobile ? 7 : 10
        const leftLength = isMobile ? 3 : 4
        const rightLength = totalLength - leftLength - 1

        // 左右側是否需要隱藏
        const isLeftClamped = totalPage > totalLength && currentPage - 1 > leftLength
        const isRightClamped = totalPage > totalLength && totalPage - currentPage > rightLength

        if (!isLeftClamped && !isRightClamped) {
          return range(1, totalPage + 1)
        }

        if (isLeftClamped && !isRightClamped) {
          return [1, '...', ...range(totalPage - totalLength + 3, totalPage + 1)]
        }

        if (!isLeftClamped && isRightClamped) {
          return [...range(1, totalLength - 1), '...', totalPage]
        }

        return [
          1,
          '...',
          ...range(currentPage - leftLength + 2, currentPage + rightLength - 1),
          '...',
          totalPage,
        ]
      }

      const numberButtons = createNumberButtons()
      const hasPrev = currentPage !== 1
      const hasNext = currentPage !== totalPage

      return (
        <>
          <PrevButton
            href={hasPrev ? pageUrl(currentPage - 1) : undefined}
            rel={hasPrev && shouldNoFollow?.(currentPage - 1) ? 'nofollow' : undefined}
          />
          {numberButtons.map((page, key) => (
            <NumberButton
              key={key}
              active={page === currentPage}
              href={page !== '...' ? pageUrl(page) : undefined}
              rel={page !== '...' && shouldNoFollow?.(page) ? 'nofollow' : undefined}
            >
              {page}
            </NumberButton>
          ))}
          <NextButton
            href={hasNext ? pageUrl(currentPage + 1) : undefined}
            rel={hasNext && shouldNoFollow?.(currentPage + 1) ? 'nofollow' : undefined}
          />
        </>
      )
    },
    [currentPage, totalPage, pageUrl, shouldNoFollow],
  )

  return (
    <>
      <MobileContainer>{renderButtons(true)}</MobileContainer>
      <DesktopContainer>{renderButtons(false)}</DesktopContainer>
    </>
  )
}

export default Pagination
